import React from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { RCModal } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import SellerListingCardsToolbarContext from '../../SellerListingCardsToolbarContext';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingsCollectionContext from '../../SellerListingsCollectionContext';

export const COMPONENT_NAME = 'BulkDeleteAffirmModal';

const BULK_DELETE_AFFIRM_MUTATION = gql(`
  mutation Core_MyListings_BulkDeleteAffirm(
    $input: Input_core_apimessages_BulkDeleteZeroPercentAffirmRequest
  ) {
    bulkDeleteZeroPercentAffirm(input: $input) {
      responses {
        listingId
        success
        errors {
          message
        }
      }
    }
  }
`);

const BULK_DELETE_AFFIRM_MUTATION_ASYNC = gql(`
  mutation Core_MyListings_BulkDeleteAffirmAsync(
    $input: Input_core_apimessages_BulkDeleteZeroPercentAffirmAsyncRequest
  ) {
    bulkDeleteZeroPercentAffirmAsync(input: $input) {
      enqueued
      message
    }
  }
`);

function isEligibleForBulkAction(listing) {
  const { affirmZeroPercentFinancingConfiguration } = listing;

  const {
    isEnabled,
  } = affirmZeroPercentFinancingConfiguration;

  return isEnabled;
}

export default function BulkDeleteAffirmModal({
  closeModal,
  selectedListings,
}) {
  const {
    listingsCollectionState,
  } = React.useContext(SellerListingsCollectionContext);

  const {
    hasAllMatchesSelected,
    totalMatchesEnabledForAffirm,
    serializedDashboardSearchParams,
  } = listingsCollectionState;

  const {
    handleBulkResponses,
    handleGenericError,
    handleAsyncBulkResponse,
  } = React.useContext(SellerListingCardsToolbarContext);

  const [bulkDeleteAffirm, {
    data = {},
    loading,
    errors,
  }] = useMutation(BULK_DELETE_AFFIRM_MUTATION);
  const hasErrors = !!errors.length;

  const [bulkDeleteAffirmAsync, {
    data: asyncData = {},
    loading: asyncLoading,
    errors: asyncErrors,
  }] = useMutation(BULK_DELETE_AFFIRM_MUTATION_ASYNC);
  const hasAsyncErrors = !!asyncErrors.length;

  const enabledListings = selectedListings.filter(listing => isEligibleForBulkAction(listing));

  const enabledCount = hasAllMatchesSelected ? totalMatchesEnabledForAffirm : enabledListings.length;
  const hasEnabledListings = !!enabledCount;

  React.useEffect(() => {
    if (loading) return;
    if (hasErrors) return handleUnexpectedError();

    handleResponses();
  }, [loading]);

  React.useEffect(() => {
    if (asyncLoading) return;
    if (hasAsyncErrors) return handleUnexpectedError();

    handleAsyncResponse();
  }, [asyncLoading]);

  function handleResponses() {
    if (data?.bulkDeleteZeroPercentAffirm) {
      const { responses } = data.bulkDeleteZeroPercentAffirm;

      handleBulkResponses({
        responses,
        I18nSuccessMessageKey: 'discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.successes',
        I18nErrorMessageKey: 'discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.errors.title',
      });

      closeModal();
    }
  }

  function handleAsyncResponse() {
    if (asyncData?.bulkDeleteZeroPercentAffirmAsync) {
      handleAsyncBulkResponse({
        I18nKey: 'discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.asyncResponses.successes',
        expectedTotal: enabledCount,
      });

      closeModal();
    }
  }

  function handleUnexpectedError() {
    handleGenericError();
    closeModal();
  }

  function submit() {
    trackEvent({
      componentName: COMPONENT_NAME,
      eventName: MParticleEventName.SellerBulkDeletingZeroPercentAffirm,
      listingsCount: enabledCount,
      hasAllMatchesSelected,
    });

    if (hasAllMatchesSelected) {
      bulkDeleteAffirmAsync({
        variables: {
          input: {
            expectedTotal: enabledCount,
            serializedDashboardSearchParams,
          },
        },
      });
    } else {
      const listingIds = enabledListings.map(listing => listing.id);

      bulkDeleteAffirm({
        variables: {
          input: {
            listingIds,
          },
        },
      });
    }
  }

  return (
    <RCModal
      isOpen
      onOpenChange={(open) => { if (!open) closeModal(); }}
      title={I18n.t('discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.headerTitle')}
      actions={{
        primary: {
          buttonText: I18n.t('discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.submit'),
          disabled: !hasEnabledListings,
          loading: loading || asyncLoading,
          onClick: submit,
        },
        secondary: true,
      }}
    >
      <div className="d-flex fx-dir-col gap-4">
        <div>
          {I18n.t('discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.description')}
        </div>

        <Translate
          html
          text="discovery.listingsManagement.toolbar.bulkActions.affirm.deleteModal.actions"
          args={{
            count: enabledCount,
            formattedCount: I18n.toNumber(enabledCount, {
              precision: 0,
            }),
          }}
        />
      </div>
    </RCModal>
  );
}
